// Lightweight dip-site store. Unlike the full sync stores (aircraft,
// pilots, waypoints) which carry IDB + Supabase round-tripping, dip
// sites are low-volume + multi-pilot-shared so we just fetch from
// Supabase on demand. Realtime publication updates the list when
// another pilot in the org adds a site.

import { create } from 'zustand';
import { listDipSites, type DipSite } from '../lib/dipSites';
import { supabase } from '../lib/supabase';

interface DipSiteState {
  list: DipSite[];
  loaded: boolean;
  loading: boolean;
  /** Pull from Supabase. Safe to call repeatedly; debounces. */
  refresh: () => Promise<void>;
  /** Wire a realtime subscription so the list updates when any other
   *  pilot in the org adds / edits / deletes a dip site. Returns an
   *  unsubscribe function. */
  subscribe: () => () => void;
  /** Replace one row in the list (used after a save). */
  upsertLocal: (site: DipSite) => void;
  /** Drop one row in the list (used after a delete). */
  removeLocal: (id: string) => void;
}

let inflight: Promise<void> | null = null;

export const useDipSiteStore = create<DipSiteState>((set, get) => ({
  list: [],
  loaded: false,
  loading: false,

  refresh: async () => {
    if (inflight) return inflight;
    inflight = (async () => {
      set({ loading: true });
      try {
        const next = await listDipSites();
        set({ list: next, loaded: true, loading: false });
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  },

  subscribe: () => {
    if (!supabase) return () => {};
    const sb = supabase;
    const ch = sb
      .channel('dip-sites-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'app', table: 'dip_sites' },
        () => { void get().refresh(); },
      )
      .subscribe();
    return () => { void sb.removeChannel(ch); };
  },

  upsertLocal: (site) => {
    set((s) => {
      const idx = s.list.findIndex((x) => x.id === site.id);
      if (idx < 0) return { list: [site, ...s.list] };
      const next = s.list.slice();
      next[idx] = site;
      return { list: next };
    });
  },

  removeLocal: (id) => {
    set((s) => ({ list: s.list.filter((x) => x.id !== id) }));
  },
}));
